<template>
    <span
        v-if="status"
        class="workflow-save-status"
        :class="[
            `workflow-save-status--${status}`,
            { 'workflow-save-status--with-text': showText },
        ]"
        role="status"
        aria-live="polite"
        :aria-label="statusLabel"
    >
        <BSpinner
            v-if="status === 'saving'"
            small
            class="workflow-save-status__icon"
        />
        <FeatherIcon
            v-else
            :icon="statusIcon"
            size="15"
            class="workflow-save-status__icon"
        />
        <span v-if="showText">{{ statusLabel }}</span>
    </span>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'

const SAVED_STATUS_DURATION = 2000

export default {
    name: 'WorkflowSaveStatus',
    components: {
        BSpinner,
    },
    props: {
        step: {
            type: Object,
            required: true,
        },
        showText: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            status: null,
            hasSaveActivity: false,
            savedStatusTimeout: null,
        }
    },
    computed: {
        statusIcon() {
            return this.status === 'saved' ? 'CheckIcon' : 'ClockIcon'
        },
        statusLabel() {
            const labels = {
                pending: {
                    key: 'Page(ReceivedRequest):Component(WorkflowSaveStatus):pending',
                    fallback: 'Changes pending…',
                },
                saving: {
                    key: 'Page(ReceivedRequest):Component(WorkflowSaveStatus):saving',
                    fallback: 'Saving…',
                },
                saved: {
                    key: 'Page(ReceivedRequest):Component(WorkflowSaveStatus):saved',
                    fallback: 'Saved',
                },
            }
            const label = labels[this.status]

            return label ? this.translate(label.key, label.fallback) : ''
        },
    },
    watch: {
        step: {
            immediate: true,
            handler(step, previousStep) {
                if (!previousStep || previousStep.id !== step.id) {
                    this.resetStatus()
                }

                this.syncStatus(step)
            },
        },
    },
    beforeDestroy() {
        clearTimeout(this.savedStatusTimeout)
    },
    methods: {
        resetStatus() {
            clearTimeout(this.savedStatusTimeout)
            this.savedStatusTimeout = null
            this.hasSaveActivity = false
            this.status = null
        },
        syncStatus(step) {
            if (step.error) {
                this.resetStatus()
                return
            }

            if (step.isSaving) {
                clearTimeout(this.savedStatusTimeout)
                this.savedStatusTimeout = null
                this.hasSaveActivity = true
                this.status = 'saving'
                return
            }

            if (step.isDirty) {
                clearTimeout(this.savedStatusTimeout)
                this.savedStatusTimeout = null
                this.hasSaveActivity = true
                this.status = 'pending'
                return
            }

            if (this.hasSaveActivity) {
                this.hasSaveActivity = false
                this.status = 'saved'
                this.savedStatusTimeout = setTimeout(() => {
                    this.status = null
                    this.savedStatusTimeout = null
                }, SAVED_STATUS_DURATION)
            }
        },
        translate(key, fallback) {
            if (typeof this.$t !== 'function') {
                return fallback
            }

            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
    },
}
</script>

<style lang="scss" scoped>
.workflow-save-status {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    margin-left: 1rem;
    color: #6e6b7b;
    font-size: .875rem;
    font-weight: 400;
    white-space: nowrap;
}

.workflow-save-status--saved {
    color: #28c76f;
}

.workflow-save-status--with-text .workflow-save-status__icon {
    margin-right: .4rem;
}
</style>
